import type { IProperty, ILead } from 'src/services/api';
import { ELeadStage } from 'src/services/api/leads/types';
import type { IConversation } from 'src/api/landlord-messages-api';
import { EConversationTab, type TLandlordConversationsContext } from 'src/pages/landlord/conversations/context/types';
import { searchConversations } from 'src/pages/landlord/conversations/utils/search-conversations';
import { normalizeSource } from 'src/pages/landlord/analytics/utils/normalize-source';
import { type TConversationsFilter } from 'src/services/api/messages/use-conversations-search';

const filterByTab = (
    leadsByUuid: Map<string, ILead>,
    conversations: IConversation[],
    tab: TLandlordConversationsContext['tab'],
) => {
    return conversations.filter((conversation) => {
        const lead = leadsByUuid.get(conversation.lead_uuid);
        if (!lead) { return false; }

        switch (tab) {
            case EConversationTab.ALL:
                return true;
            case EConversationTab.NEW:
                return lead.stage === ELeadStage.NEW;
            case EConversationTab.CONTACTED:
                // Product decision: a lead that replied after being contacted ("responsive") is
                // still grouped under the Contacted tab, not a separate tab.
                return [ELeadStage.CONTACTED, ELeadStage.RESPONSIVE].includes(lead.stage);
            case EConversationTab.TOUR:
                return [ELeadStage.TOUR_REQUESTED, ELeadStage.TOUR_SCHEDULED, ELeadStage.TOUR_COMPLETED]
                    .includes(lead.stage);
            case EConversationTab.APPLICATION:
                return [ELeadStage.INVITED_TO_APPLY, ELeadStage.APPLICATION_COMPLETED, ELeadStage.APPLICATION_APPROVED, ELeadStage.LEASE_SIGNED]
                    .includes(lead.stage);
            case EConversationTab.NOT_QUALIFIED:
                return lead.stage === ELeadStage.NOT_QUALIFIED;
            case EConversationTab.NOT_INTERESTED:
                return lead.stage === ELeadStage.NOT_INTERESTED;
            default: {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const _assertNever: never = tab;
            }
        }
    });
};

const filterByAssignee = (
    leadsByUuid: Map<string, ILead>,
    conversations: IConversation[],
    assigneeIds: string[],
    isAssigneeExempt?: (conversation: IConversation, lead?: ILead) => boolean,
) => {
    if (!assigneeIds.length) {
        return conversations;
    }

    return conversations.filter((conversation) => {
        const lead = leadsByUuid.get(conversation.lead_uuid);
        if (isAssigneeExempt?.(conversation, lead)) {
            return true;
        }
        return assigneeIds.includes(String(lead?.owner));
    });
};

/**
 * NOTE: ingestion source filter works over both normalized and raw ingestion source values
 * But it must be an exact match
 */
const filterByIngestionSources = (
    leadsByUuid: Map<string, ILead>,
    conversations: IConversation[],
    ingestionSources: string[],
) => {
    if (!ingestionSources.length) {
        return conversations;
    }

    return conversations.filter((conversation) => {
        const lead = leadsByUuid.get(conversation.lead_uuid);
        if (!lead || !lead.ingestion_source) {
            return false;
        }
        if (ingestionSources.includes(lead.ingestion_source)) {
            // raw match
            return true;
        }
        // normalized match
        return ingestionSources.includes(normalizeSource(String(lead?.ingestion_source)));
    });
};

const filterByLeadStages = (
    leadsByUuid: Map<string, ILead>,
    conversations: IConversation[],
    leadStages: ELeadStage[],
) => {
    if (!leadStages.length) {
        return conversations;
    }

    return conversations.filter((conversation) => {
        const lead = leadsByUuid.get(conversation.lead_uuid);
        if (!lead) { return false; }
        return leadStages.includes(lead.stage);
    });
};

const filterByLeadLabels = (
    leadsByUuid: Map<string, ILead>,
    conversations: IConversation[],
    leadLabelIds: string[],
) => {
    if (!leadLabelIds.length) {
        return conversations;
    }

    return conversations.filter((conversation) => {
        return leadLabelIds.some((filterLabelId) => {
            const lead = leadsByUuid.get(conversation.lead_uuid);
            return lead?.labels.some((label) => label.id === Number(filterLabelId));
        });
    });
};

const filterByExcludedLeadLabels = (
    leadsByUuid: Map<string, ILead>,
    conversations: IConversation[],
    excludeLeadLabelIds: string[],
) => {
    if (!excludeLeadLabelIds.length) {
        return conversations;
    }

    return conversations.filter((conversation) => {
        return excludeLeadLabelIds.every((filterLabelId) => {
            const lead = leadsByUuid.get(conversation.lead_uuid);
            return !lead?.labels.some((label) => label.id === Number(filterLabelId));
        });
    });
};

const filterByProperty = (
    conversations: IConversation[],
    propertyIds: string[],
    hasNoProperty?: boolean,
) => {
    let filteredConversations = conversations;

    if (hasNoProperty) {
        filteredConversations = conversations.filter((conversation) => {
            return !conversation.property_id;
        });
    }

    if (propertyIds.length) {
        filteredConversations = filteredConversations.filter((conversation) => {
            return propertyIds.includes(String(conversation.property_id));
        });
    }

    return filteredConversations;
};

const filterByLastMessageDirection = (
    conversations: IConversation[],
    lastMessageDirection: 'in' | 'out' | undefined,
) => {
    if (!lastMessageDirection) { return conversations; }
    return conversations.filter((c) => c.last_message_direction === lastMessageDirection);
};

const filterByPropertyLabels = (conversations: IConversation[], properties: IProperty[], propertyLabelIds: string[]) => {
    if (!propertyLabelIds.length) {
        return conversations;
    }

    return conversations.filter((conversation) => {
        const property = properties.find((o) => o.id === conversation.property_id);
        return propertyLabelIds.some((filterLabelId) => {
            return property?.labels.some((label) => label.id === Number(filterLabelId));
        });
    });
};

/**
 * Return true if the lead should still be shown
 * Do not show the lead if the lead has a hidden lead label.
 * Unless we are filtering by lead labels, in which case we show all leads (other functions filter down by the actual labels).
 */
export const filterLeadByHiddenLabels = (
    lead: ILead,
    hiddenLeadLabelIds: number[] | undefined,
    isFilteringByLeadLabels: boolean,
): boolean => {
    if (isFilteringByLeadLabels) {
        return true;
    }
    if (hiddenLeadLabelIds === undefined) {
        return true;
    }

    return !lead.labels.some((label) => hiddenLeadLabelIds.includes(label.id));
};

/**
 * Remove all keys that map to null values from the inbox filter.
 * Only set values should be present.
 */
export const omitNullsFromInboxFilter = (inboxFilter: TConversationsFilter): TConversationsFilter => {
    if (inboxFilter === null || inboxFilter === undefined) {
        throw new Error('Filter must be non-null/defined here');
    }
    const newFilter = Object.fromEntries(
        Object.entries(inboxFilter).filter(([_key, value]) => {
            return value !== null;

        }));

    return newFilter;
};

/**
 * @param hiddenLeadLabelIds - Hide leads with these labels in conversations UNLESS a label filter is applied
 * @param search If empty, do not filter by search query
 */
export const filterConversations = ({
    hiddenLeadLabelIds,
    properties,
    leadsByUuid,
    conversations,
    tab,
    search,
    filter,
    isAssigneeExempt,
}: {
    hiddenLeadLabelIds: number[] | undefined;
    properties: IProperty[] | undefined;
    leadsByUuid: Map<string, ILead>;
    conversations: IConversation[] | undefined;
    tab: TLandlordConversationsContext['tab'];
    search: TLandlordConversationsContext['search'];
    filter: TConversationsFilter;
    /**
     * Conversations for which this returns ``true`` bypass the assignee filter
     */
    isAssigneeExempt?: (conversation: IConversation, lead?: ILead) => boolean;
}) => {
    if (!properties || !conversations) {
        return [];
    }

    let filteredConversations = conversations;

    const isFilteringByLeadLabels = filter.leadLabelIds !== undefined && filter.leadLabelIds.length > 0;
    filteredConversations = filteredConversations.filter((conversation) => {
        const lead = leadsByUuid.get(conversation.lead_uuid);
        if (!lead) {
            return false;
        }
        return filterLeadByHiddenLabels(lead, hiddenLeadLabelIds, isFilteringByLeadLabels);
    });

    filteredConversations = filterByTab(leadsByUuid, filteredConversations, tab);
    filteredConversations = filterByAssignee(leadsByUuid, filteredConversations, filter.assigneeIds ?? [], isAssigneeExempt);
    filteredConversations = filterByIngestionSources(leadsByUuid, filteredConversations, filter.ingestionSources ?? []);
    filteredConversations = filterByLeadStages(leadsByUuid, filteredConversations, filter.leadStages ?? []);
    // Include and exclude label sets must be disjoint: a label cannot be both "is" and "is not".
    // Enforce it here so a stale or overlapping filter can't contradict itself (include keeps a
    // lead, exclude then drops it). Excluding narrows, so it wins over including.
    const excludeLeadLabelIds = filter.excludeLeadLabelIds ?? [];
    const excludeLeadLabelIdSet = new Set(excludeLeadLabelIds);
    const includeLeadLabelIds = (filter.leadLabelIds ?? []).filter(id => !excludeLeadLabelIdSet.has(id));
    filteredConversations = filterByLeadLabels(leadsByUuid, filteredConversations, includeLeadLabelIds);
    filteredConversations = filterByExcludedLeadLabels(leadsByUuid, filteredConversations, excludeLeadLabelIds);
    filteredConversations = filterByProperty(filteredConversations, filter.propertyIds ?? [], filter.hasNoProperty);
    filteredConversations = filterByPropertyLabels(filteredConversations, properties, filter.propertyLabelIds ?? []);
    filteredConversations = filterByLastMessageDirection(filteredConversations, filter.lastMessageDirection);

    filteredConversations = searchConversations(leadsByUuid, filteredConversations, search);

    return filteredConversations;
};
